import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Database, Share2, Lock, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';

export function PrivacyPolicy() {
  const lastUpdated = "January 29, 2026";
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            <h1 className="font-serif text-xl font-medium">Privacy Policy</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="prose prose-sm max-w-none">
          <p className="text-muted-foreground text-sm mb-8">
            Last updated: {lastUpdated}
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              MindDeck ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our 
              mobile application and web service (collectively, the "Service").
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Please read this Privacy Policy carefully. By using the Service, you agree to the collection 
              and use of information in accordance with this policy.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Information We Collect */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Database className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-2xl font-medium">Information We Collect</h2>
            </div>
            
            <h3 className="font-medium text-lg mt-6 mb-3">Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Account Information:</strong> Email address, name (optional), and password when you create an account</li>
              <li><strong>Profile Information:</strong> Username for sharing features (optional)</li>
              <li><strong>Content:</strong> Cards, decks, packets, notes, fields, and any other content you create within the Service</li>
              <li><strong>Third-Party Credentials:</strong> If you choose to connect external services (Airtable, Google Sheets), we store the necessary credentials to enable those integrations</li>
            </ul>

            <h3 className="font-medium text-lg mt-6 mb-3">Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Usage Data:</strong> Recently viewed cards, feature usage patterns</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers</li>
              <li><strong>Log Data:</strong> IP address, access times, and pages viewed</li>
            </ul>

            <h3 className="font-medium text-lg mt-6 mb-3">Information from Third Parties</h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Google Sign-In:</strong> If you use Google Sign-In, we receive your email, name, and profile picture from Google</li>
              <li><strong>Google Drive:</strong> If you choose to backup your data to Google Drive, we request access to create and read backup files in your Google Drive. We only access files created by MindDeck - we do not access any other files in your Drive.</li>
            </ul>
          </section>

          <Separator className="my-8" />

          {/* How We Use Information */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-2xl font-medium">How We Use Your Information</h2>
            </div>
            <p className="text-muted-foreground mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide, maintain, and improve the Service</li>
              <li>Create and manage your account</li>
              <li>Process and store your cards and content</li>
              <li>Enable sharing features and deck imports</li>
              <li>Send password reset emails and important service notifications</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Monitor and analyze usage patterns to improve user experience</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
            </ul>
          </section>

          <Separator className="my-8" />

          {/* Data Sharing */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Share2 className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-2xl font-medium">How We Share Your Information</h2>
            </div>
            <p className="text-muted-foreground mb-4">We do not sell your personal information. We may share your information in the following situations:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>With Your Consent:</strong> When you explicitly choose to share decks publicly or with specific users</li>
              <li><strong>Service Providers:</strong> With third-party services that help us operate (email delivery, hosting)</li>
              <li><strong>Third-Party Integrations:</strong> When you connect to external services like Google Sheets or Airtable, your card data is shared with those services as you direct</li>
              <li><strong>Legal Requirements:</strong> If required by law or to protect our rights and users' safety</li>
            </ul>
          </section>

          <Separator className="my-8" />

          {/* Data Security */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your 
              personal information. This includes encryption of data in transit (HTTPS), secure password 
              hashing, and access controls. However, no method of transmission over the Internet is 100% 
              secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Data Retention */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Trash2 className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-2xl font-medium">Data Retention & Deletion</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to 
              provide you services. You can delete your cards and content at any time. If you wish to 
              delete your account entirely, please contact us and we will delete your data within 30 days, 
              except where we are required to retain it for legal purposes.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Your Rights</h2>
            <p className="text-muted-foreground mb-4">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your data</li>
              <li><strong>Export:</strong> Export your cards and decks in JSON or CSV format</li>
              <li><strong>Withdraw Consent:</strong> Disconnect third-party integrations at any time</li>
            </ul>
          </section>

          <Separator className="my-8" />

          {/* Children's Privacy */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              The Service is not intended for children under the age of 13. We do not knowingly collect 
              personal information from children under 13. If we become aware that we have collected 
              personal information from a child under 13, we will take steps to delete that information.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Changes */}
          <section className="mb-8">
            <h2 className="font-serif text-2xl font-medium mb-4">Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Last updated" date. You are 
              advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <Separator className="my-8" />

          {/* Contact */}
          <section className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-5 w-5 text-primary" />
              <h2 className="font-serif text-2xl font-medium">Contact Us</h2>
            </div>
            <p className="text-muted-foreground leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="text-sm">
                <strong>Email:</strong> <a href="mailto:emaildtdm@gmail.com" className="text-primary hover:underline">emaildtdm@gmail.com</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} MindDeck. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
